/**
 * StatusFeedback Component
 *
 * Visual feedback for success/error/warning states with pulse animation.
 * Uses shared animation keyframes from _animations.css.
 *
 * @package PresszoneComments
 */

import { el } from '../utils/dom.js';

/**
 * Create a status feedback element with visual feedback animation.
 *
 * @param {Object} options
 * @param {string} [options.type='success'] - Status type: success, error, warning
 * @param {HTMLElement|string} [options.children] - Optional content (icon, checkmark, etc.)
 * @param {string} [options.className=''] - Additional CSS classes
 * @returns {HTMLElement}
 */
export function StatusFeedback({ type = 'success', children, className = '' } = {}) {
    const typeClass = `multilingual-status-feedback--${type}`;

    const wrapper = el('span', {
        class: `multilingual-status-feedback ${typeClass} ${className}`.trim()
    });

    if (children) {
        if (children instanceof Node) {
            wrapper.appendChild(children);
        } else if (typeof children === 'string') {
            wrapper.textContent = children;
        }
    }

    return wrapper;
}

/**
 * Create a success checkmark with animation.
 *
 * @param {Object} [options]
 * @param {string} [options.className=''] - Additional CSS classes
 * @returns {HTMLElement}
 */
export function SuccessCheckmark({ className = '' } = {}) {
    return StatusFeedback({
        type: 'success',
        children: el('span', { style: { fontSize: '16px' } }, '\u2714'),
        className
    });
}

/**
 * Create an error indicator with animation.
 *
 * @param {Object} [options]
 * @param {string} [options.className=''] - Additional CSS classes
 * @returns {HTMLElement}
 */
export function ErrorIndicator({ className = '' } = {}) {
    return StatusFeedback({
        type: 'error',
        children: el('span', { style: { fontSize: '16px' } }, '\u2716'),
        className
    });
}

/**
 * Create a warning indicator with animation.
 *
 * @param {Object} [options]
 * @param {string} [options.className=''] - Additional CSS classes
 * @returns {HTMLElement}
 */
export function WarningIndicator({ className = '' } = {}) {
    return StatusFeedback({
        type: 'warning',
        children: el('span', { style: { fontSize: '16px' } }, '\u26A0'),
        className
    });
}

export default StatusFeedback;
